import { getApiErrorMessage } from '@/lib/utils';
// Task #1418 — Card pairing notifiche Telegram (lato utente).
// Flusso: "Genera codice" -> mostra OTP + deep-link bot -> polling 3s su
// /pair/status finche `paired === true`. "Disconnetti" rimuove il link.

import { useCallback, useEffect, useRef, useState } from 'react';
import { Send, RefreshCw, Copy, ExternalLink, Trash2, Check } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@easyflow/ui-frontend/components/ui/card';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { Badge } from '@easyflow/ui-frontend/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { apiClient } from '@/lib/api';
import type {
  TelegramPairingInitiateResponse,
  TelegramPairingStatus,
} from '@easyflow/shared';

interface InitiatedCode {
  code: string;
  expiresAt: string;
  deepLink: string | null;
  botUsername: string | null;
}

function envelope<T>(res: unknown): T | null {
  const r = res as { data?: { data?: T } | T } | null;
  if (!r) return null;
  const inner = (r.data as { data?: T } | undefined)?.data ?? (r.data as T | undefined);
  return (inner as T) ?? null;
}

function formatRemaining(expiresAt: string): string {
  const ms = new Date(expiresAt).getTime() - Date.now();
  if (ms <= 0) return 'scaduto';
  const min = Math.floor(ms / 60_000);
  const sec = Math.floor((ms % 60_000) / 1000);
  return `${min}m ${sec.toString().padStart(2, '0')}s`;
}

export default function TelegramPairingCard() {
  const { toast } = useToast();
  const [status, setStatus] = useState<TelegramPairingStatus | null>(null);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [initiating, setInitiating] = useState(false);
  const [revoking, setRevoking] = useState(false);
  const [initiated, setInitiated] = useState<InitiatedCode | null>(null);
  const [copied, setCopied] = useState(false);
  const [tick, setTick] = useState(0);
  const pollRef = useRef<number | null>(null);

  const loadStatus = useCallback(async () => {
    try {
      const res = await apiClient.get('/notifications/telegram/pair/status');
      const next = envelope<TelegramPairingStatus>(res);
      if (next) setStatus(next);
    } catch {
      // silenzioso: lo stato si reidrata al prossimo tick
    } finally {
      setLoadingStatus(false);
    }
  }, []);

  useEffect(() => { void loadStatus(); }, [loadStatus]);

  // Polling 3s mentre c'e un codice attivo non ancora consumato.
  useEffect(() => {
    if (!initiated || (status && status.paired)) {
      if (pollRef.current) { window.clearInterval(pollRef.current); pollRef.current = null; }
      return;
    }
    pollRef.current = window.setInterval(() => { void loadStatus(); }, 3000);
    return () => { if (pollRef.current) { window.clearInterval(pollRef.current); pollRef.current = null; } };
  }, [initiated, status, loadStatus]);

  // Tick locale 1s per countdown.
  useEffect(() => {
    if (!initiated) return;
    const id = window.setInterval(() => setTick(t => t + 1), 1000);
    return () => window.clearInterval(id);
  }, [initiated]);

  // Quando il pairing si completa, dismetti il codice mostrato.
  useEffect(() => {
    if (status?.paired && initiated) {
      setInitiated(null);
      toast({ title: 'Telegram collegato', description: 'Riceverai le notifiche selezionate anche su Telegram.' });
    }
  }, [status?.paired, initiated, toast]);

  const handleInitiate = useCallback(async () => {
    setInitiating(true);
    try {
      const res = await apiClient.post('/notifications/telegram/pair/initiate', {});
      const data = envelope<TelegramPairingInitiateResponse>(res);
      if (!data || !data.code) throw new Error('Risposta non valida');
      setInitiated({
        code: data.code,
        expiresAt: data.expires_at,
        deepLink: data.deep_link,
        botUsername: data.bot_username,
      });
      void loadStatus();
    } catch (err) {
      const msg = getApiErrorMessage(err)
        ?? (err as Error).message
        ?? 'Errore generazione codice';
      toast({ title: 'Impossibile generare il codice', description: msg, variant: 'destructive' });
    } finally {
      setInitiating(false);
    }
  }, [loadStatus, toast]);

  const handleRevoke = useCallback(async () => {
    setRevoking(true);
    try {
      await apiClient.post('/notifications/telegram/pair/revoke', {});
      setInitiated(null);
      await loadStatus();
      toast({ title: 'Telegram scollegato', description: 'Le notifiche su Telegram sono disattivate.' });
    } catch (err) {
      const msg = (err as Error).message ?? 'Errore disconnessione';
      toast({ title: 'Errore', description: msg, variant: 'destructive' });
    } finally {
      setRevoking(false);
    }
  }, [loadStatus, toast]);

  const handleCopy = useCallback(async () => {
    if (!initiated) return;
    try {
      await navigator.clipboard.writeText(initiated.code);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      toast({ title: 'Copia non riuscita', description: 'Copia manualmente il codice.', variant: 'destructive' });
    }
  }, [initiated, toast]);

  // `tick` consumed by countdown render — no-op reference.
  void tick;

  const noActiveBot = status && !status.has_active_bot;

  return (
    <Card data-testid="telegram-pairing-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Send className="h-5 w-5" /> Notifiche Telegram
          {status?.paired && <Badge variant="default" className="ml-2">Attivo</Badge>}
        </CardTitle>
        <CardDescription>
          Collega il tuo account Telegram per ricevere le notifiche selezionate direttamente in chat.
          Attiva il veicolo <em>Telegram</em> nella matrice qui sopra per le categorie che ti interessano.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {loadingStatus ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <RefreshCw className="h-4 w-4 animate-spin" /> Caricamento stato pairing…
          </div>
        ) : noActiveBot ? (
          <div className="rounded-md border border-dashed p-4 text-sm">
            Nessun bot Telegram attivo per il tuo tenant. Chiedi all'amministratore di registrare un bot
            in <em>Impostazioni › Integrazioni › Telegram</em>.
          </div>
        ) : status?.paired ? (
          <div className="space-y-3">
            <div className="rounded-md border bg-muted/30 px-3 py-3 text-sm">
              <div className="font-medium">
                Collegato {status.telegram_username ? <>come <span className="font-mono">@{status.telegram_username}</span></> : null}
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                {status.paired_at ? `Pairing del ${new Date(status.paired_at).toLocaleString('it-IT')}` : ''}
                {status.bot_username ? ` · bot @${status.bot_username}` : ''}
              </div>
            </div>
            <div className="flex justify-end">
              <Button
                variant="outline"
                size="sm"
                onClick={handleRevoke}
                disabled={revoking}
                data-testid="telegram-revoke-btn"
              >
                {revoking ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
                Disconnetti
              </Button>
            </div>
          </div>
        ) : initiated ? (
          <div className="space-y-3">
            <div className="rounded-md border bg-muted/30 px-3 py-3">
              <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Il tuo codice</div>
              <div className="flex items-center gap-3">
                <code className="text-2xl font-mono font-bold tracking-widest" data-testid="telegram-pairing-code">
                  {initiated.code}
                </code>
                <Button type="button" variant="ghost" size="sm" onClick={handleCopy}>
                  {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                </Button>
                <span className="text-xs text-muted-foreground ml-auto">
                  Scade tra {formatRemaining(initiated.expiresAt)}
                </span>
              </div>
            </div>
            <ol className="space-y-2 text-sm pl-4 list-decimal">
              <li>
                Apri il bot Telegram
                {initiated.botUsername ? <> <span className="font-mono">@{initiated.botUsername}</span></> : ' del tuo tenant'}.
              </li>
              <li>
                Invia il comando <code className="font-mono">/start {initiated.code}</code>.
              </li>
              <li>
                Lo stato qui sopra si aggiornera automaticamente a "Attivo".
              </li>
            </ol>
            {initiated.deepLink && (
              <div className="flex justify-start">
                <Button asChild variant="outline" size="sm">
                  <a href={initiated.deepLink} target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="h-4 w-4 mr-2" /> Apri il bot in Telegram
                  </a>
                </Button>
              </div>
            )}
            <div className="flex justify-end">
              <Button
                variant="outline"
                size="sm"
                onClick={handleInitiate}
                disabled={initiating}
              >
                {initiating ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
                Genera nuovo codice
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Genera un codice OTP usa-e-getta da inviare al bot Telegram del tuo tenant per
              autorizzare la consegna delle notifiche.
            </p>
            <div className="flex justify-start">
              <Button onClick={handleInitiate} disabled={initiating} data-testid="telegram-initiate-btn">
                {initiating ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
                Genera codice di pairing
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
